import type {
  DismissedDuplicatePerson,
  PersonId,
  RecordHint,
  ResearchHelp,
  PossibleDuplicatePersonSuggestion,
} from '@fs/zion-tree-types'
import { useRecordHints, usePossibleDuplicates, useDismissedPossibleDuplicates } from '@fs/zion-research-help'
import { useRecordHintLevelPref } from '../providers/RecordHintLevelPrefProvider'
import removeNulls from './removeNulls'

export default function useResearchHelpData(
  personId: PersonId,
  researchHelp: ResearchHelp | undefined,
  isResearcherGroup: boolean | undefined,
  treeId?: string
): ResearchHelpData {
  // We use the person ID here instead of the person so we can start fetching this data immediately. The focus person might not be fetched completely yet.
  const [{ recordHintLevel }] = useRecordHintLevelPref()
  const { data: recordHints, refetch: refetchRecordHints } = useRecordHints(
    personId,
    undefined,
    recordHintLevel === 3,
    treeId
  )
  const {
    data: duplicatePersonDataProblems = [],
    refetch: refetchDuplicates,
    loading: loadingPossibleDuplicates,
  } = usePossibleDuplicates(personId, isResearcherGroup, treeId)

  const {
    data: duplicatePersonDataProblemsDismissed = [],
    refetch: refetchDismissedDuplicates,
    loading: loadingDismissedPossibleDuplicates,
  } = useDismissedPossibleDuplicates(personId, isResearcherGroup)

  const dismissedIds = duplicatePersonDataProblemsDismissed?.map((dismissed) => dismissed.contextId)
  const filteredDuplicatePersonDataProblems = treeId
    ? duplicatePersonDataProblems?.filter((dup) => {
        return !dismissedIds.includes(dup.contextId)
      })
    : duplicatePersonDataProblems

  const count =
    (researchHelp?.childDataProblems?.length ?? 0) +
    (researchHelp?.duplicateChildDataProblems?.length ?? 0) +
    (researchHelp?.duplicateSpouseDataProblems?.length ?? 0) +
    (researchHelp?.focusPerson?.dataQualityList?.length ?? 0) +
    (researchHelp?.focusPerson?.researchSuggestionList?.length ?? 0) +
    (researchHelp?.couples?.dataQualityList?.length ?? 0) +
    (researchHelp?.couples?.researchSuggestionList?.length ?? 0) +
    filteredDuplicatePersonDataProblems.length +
    (recordHints?.matches.length ?? 0)

  const dismissedCount =
    (researchHelp?.childDataProblemsDismissed?.length ?? 0) +
    (researchHelp?.duplicateChildDataProblemsDismissed?.length ?? 0) +
    (researchHelp?.focusPersonDismissed?.dataQualityList?.length ?? 0) +
    (researchHelp?.focusPersonDismissed?.researchSuggestionList?.length ?? 0) +
    (researchHelp?.couplesDismissed?.dataQualityList?.length ?? 0) +
    (researchHelp?.couplesDismissed?.researchSuggestionList?.length ?? 0) +
    duplicatePersonDataProblemsDismissed.length +
    (recordHints?.nonMatches.length ?? 0)

  return {
    ...researchHelp,
    duplicatePersonDataProblems: removeNulls(filteredDuplicatePersonDataProblems),
    duplicatePersonDataProblemsDismissed: removeNulls(duplicatePersonDataProblemsDismissed),
    recordHints: removeNulls(recordHints?.matches),
    recordHintsDismissed: removeNulls(recordHints?.nonMatches),
    count,
    dismissedCount,
    refetchDuplicates,
    refetchDismissedDuplicates,
    refetchRecordHints,
    loadingRecordHints: !recordHints,
    loadingPossibleDuplicates: !!loadingPossibleDuplicates,
    loadingDismissedPossibleDuplicates: !!loadingDismissedPossibleDuplicates,
  }
}

export type ResearchHelpData = ResearchHelp & {
  duplicatePersonDataProblems?: PossibleDuplicatePersonSuggestion[]
  duplicatePersonDataProblemsDismissed?: (PossibleDuplicatePersonSuggestion | DismissedDuplicatePerson)[]
  recordHints?: RecordHint[]
  recordHintsDismissed?: RecordHint[]
  count: number
  dismissedCount: number
  refetchDuplicates?: () => void
  refetchDismissedDuplicates?: () => void
  refetchRecordHints?: () => void
  loadingPossibleDuplicates: boolean
  loadingRecordHints: boolean
  loadingDismissedPossibleDuplicates: boolean
}
